import { TeamBlockedUnpaidInvoice } from "@/components/billing/team-blocked-unpaid-invoice";
import { SupportMessage } from "@/components/layout-loading/support-message";
import { ButtonTeamBillingPortal } from "@/components/team/button-team-billing-portal";
import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { Layout } from "@/Layout";
import { redirectTo } from "@/lib/location";
import { cn } from "@/lib/utils";
import { Nullable, TeamBillingPortalDTO, TeamDTO } from "@shaarp/common";
import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAdminMode } from "./admin-mode-provider";
import { LOCALSTORAGE_TEAM_SELECTED_KEY } from "./teams-provider";

type ContentManagerProps = {
  readonly teamSelected: Nullable<TeamDTO>;
  readonly isUser: boolean;
  readonly isBilling: boolean;
  /** Re-reads the teams without the loading screen (`reloadMyTeams`). */
  readonly reloadTeams: () => void;
  readonly children: ReactNode;
};

export const ContentManager = ({
  teamSelected,
  isUser,
  isBilling,
  reloadTeams,
  children,
}: ContentManagerProps): ReactNode => {
  const { isAdminMode } = useAdminMode();
  const { pathname } = useLocation();

  const now = new Date();
  const isTeamCreationSetupIncomplete =
    teamSelected?.setupTeamCreation === true;
  const isTeamTrialEndedSetupIncomplete =
    teamSelected?.trialEndsAt && now > new Date(teamSelected.trialEndsAt);
  const isTeamDisabled = teamSelected?.status === "disabled";
  const isTeamArchived = teamSelected?.status === "archived";
  // Baseline seat unpaid (billing on, payment method required, none on file).
  // Active trials keep access — enforcement only applies once there is no
  // active trial.
  const isTrialActive =
    teamSelected?.trialEndsAt && now < new Date(teamSelected.trialEndsAt);
  const isPaymentMethodMissing =
    teamSelected?.paymentMethodMissed === true && !isTrialActive;

  // referrals and checkout pages stay accessible regardless of the team's setup or billing state
  if (
    pathname === "/referrals" ||
    pathname === "/checkout-success" ||
    pathname === "/checkout-cancelled"
  ) {
    return <>{children}</>;
  }

  // have no team selected
  if (!teamSelected) {
    return <>{children}</>;
  }

  // team is archived
  if (isTeamArchived && !isAdminMode) {
    return <TeamSelectedNotFound />;
  }

  // team is disabled
  if (isTeamDisabled && !isAdminMode) {
    return <TeamSelectedDisabled />;
  }

  // team subscription deleted or no active subscription => redirect to resubscription flow
  if (
    (teamSelected?.billingError === "subscription_deleted" ||
      teamSelected?.billingError === "no_active_subscription") &&
    !isAdminMode
  ) {
    // for users
    if (isUser) {
      return <TeamSelectedBillingError isUser={isUser} />;
    }

    // for billing users
    if (
      isBilling &&
      pathname !== "/setup/resubscription-billing"
    ) {
      redirectTo("/setup/resubscription-billing");
      return;
    } else if (isBilling) {
      return <>{children}</>;
    }

    // for managers and admins
    if (pathname !== "/setup/resubscription") {
      redirectTo("/setup/resubscription");
      return;
    }

    return <>{children}</>;
  }

  // team payment failed => redirect to payment-failed flow
  if (teamSelected?.billingError === "payment_failed" && !isAdminMode) {
    // for users
    if (isUser) {
      return <TeamSelectedPaymentFailed />;
    }

    // for billing users
    if (
      isBilling &&
      pathname !== "/setup/payment-failed-billing"
    ) {
      redirectTo("/setup/payment-failed-billing");
      return;
    } else if (isBilling) {
      return <>{children}</>;
    }

    // for managers and admins
    if (pathname !== "/setup/payment-failed") {
      redirectTo("/setup/payment-failed");
      return;
    }

    return <>{children}</>;
  }

  // team has billing error (other than subscription_deleted and payment_failed) => block access for all except admin mode
  if (teamSelected?.billingError && !isAdminMode) {
    return <TeamSelectedBillingError isUser={isUser} />;
  }

  // an invoice paid by transfer is overdue (#742) => the whole team is blocked
  // until it is paid; the roles that pay it see the invoice, anyone else the
  // generic billing block, never told it is about an invoice
  if (teamSelected.blockedByUnpaidInvoice && !isAdminMode) {
    const invoice = isUser ? null : teamSelected.unpaidInvoice;
    if (!invoice) {
      return <TeamSelectedBillingError isUser />;
    }
    return (
      <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
        <TeamBlockedUnpaidInvoice invoice={invoice} onPageShown={reloadTeams} />
      </Layout>
    );
  }

  // team need complete team creation setup
  if (isTeamCreationSetupIncomplete && !isAdminMode) {
    // for users
    if (isUser) {
      return <TeamSelectedSetupCreationIncomplete />;
    }

    // for billing users
    if (
      isBilling &&
      pathname !== "/setup/team-creation-billing"
    ) {
      redirectTo("/setup/team-creation-billing");
      return;
    } else if (isBilling) {
      return <>{children}</>;
    }

    // for managers and admins
    if (pathname !== "/setup/team-creation") {
      redirectTo("/setup/team-creation");
      return;
    }

    return <>{children}</>;
  }

  // trial ended billing
  if (isTeamTrialEndedSetupIncomplete && !isAdminMode) {
    // for users
    if (isUser) {
      return <TeamSelectedSetupTrialEndedIncomplete />;
    }

    // for billing users
    if (
      isBilling &&
      pathname !== "/setup/trial-ended-billing"
    ) {
      redirectTo("/setup/trial-ended-billing");
      return;
    } else if (isBilling) {
      return <>{children}</>;
    }

    // for managers and admins
    if (pathname !== "/setup/trial-ended") {
      redirectTo("/setup/trial-ended");
      return;
    }

    return <>{children}</>;
  }

  // baseline seat unpaid => force the manager (and any billing/admin/advisor)
  // through the billing setup before granting app access. Members can't fix
  // billing and never carry this flag, so they are left untouched.
  if (isPaymentMethodMissing && !isAdminMode && !isUser) {
    if (pathname !== "/setup/team-creation-billing") {
      redirectTo("/setup/team-creation-billing");
      return;
    }

    return <>{children}</>;
  }

  if (isBilling) {
    return <UserSelectedIsBillingRole />;
  }

  return <>{children}</>;
};

const TeamSelectedNotFound = (): ReactNode => {
  const { t } = useTranslation();
  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div className={cn("flex-col py-6 space-y-3 text-center")}>
        <p className="text-lg font-bold text-center">
          {t("setup.teamSelectedNotFound.title")}
        </p>
        <Button
          onClick={() => {
            localStorage.removeItem(LOCALSTORAGE_TEAM_SELECTED_KEY);
            redirectTo("/");
          }}
        >
          {t("setup.teamSelectedNotFound.button")}
        </Button>
      </div>
    </Layout>
  );
};

const TeamSelectedDisabled = (): ReactNode => {
  const { t } = useTranslation();
  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div className={cn("flex-col py-6 space-y-3 text-center")}>
        <p className="text-lg font-bold text-center">
          {t("setup.teamSelectedDisabled.title")}
        </p>
        <SupportMessage />
      </div>
    </Layout>
  );
};

const TeamSelectedSetupCreationIncomplete = (): ReactNode => {
  const { t } = useTranslation();
  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div className={cn("flex-col py-6 space-y-3 text-center")}>
        <p className="text-lg font-bold text-center">
          {t("setup.teamSelectedSetupCreationIncomplete.line1")}
          <br />
          {t("setup.teamSelectedSetupCreationIncomplete.line2")}
          <br />
          {t("setup.teamSelectedSetupCreationIncomplete.line3")}
        </p>
        <SupportMessage />
      </div>
    </Layout>
  );
};

const TeamSelectedSetupTrialEndedIncomplete = (): ReactNode => {
  const { t } = useTranslation();
  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div className={cn("flex-col py-6 space-y-3 text-center")}>
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.teamSelectedSetupTrialEndedIncomplete.title")}
        </h1>
        <p>
          {t("setup.teamSelectedSetupTrialEndedIncomplete.line1")}
          <br />
          {t("setup.teamSelectedSetupTrialEndedIncomplete.line2")}
          <br />
          {t("setup.teamSelectedSetupTrialEndedIncomplete.line3")}
        </p>
        <SupportMessage />
      </div>
    </Layout>
  );
};

const TeamSelectedPaymentFailed = (): ReactNode => {
  const { t } = useTranslation();
  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div className={cn("flex-col py-6 space-y-3 text-center")}>
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.teamSelectedPaymentFailed.title")}
        </h1>
        <p>
          {t("setup.teamSelectedPaymentFailed.line1")}
          <br />
          {t("setup.teamSelectedPaymentFailed.line2")}
          <br />
          {t("setup.teamSelectedPaymentFailed.line3")}
        </p>
        <SupportMessage />
      </div>
    </Layout>
  );
};

const TeamSelectedBillingError = ({
  isUser,
}: {
  isUser: boolean;
}): ReactNode => {
  const { t } = useTranslation();

  const message = isUser ? (
    <>
      {t("setup.teamSelectedBillingError.user.line1")}
      <br />
      {t("setup.teamSelectedBillingError.user.line2")}
      <br />
      {t("setup.teamSelectedBillingError.user.line3")}
    </>
  ) : (
    <>
      {t("setup.teamSelectedBillingError.manager.line1")}
      <br />
      {t("setup.teamSelectedBillingError.manager.line2")}
      <br />
      <ButtonTeamBillingPortal />
    </>
  );

  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div className={cn("flex-col py-6 space-y-3 text-center")}>
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.teamSelectedBillingError.title")}
        </h1>
        <p>{message}</p>
        <SupportMessage />
      </div>
    </Layout>
  );
};

const UserSelectedIsBillingRole = (): ReactNode => {
  const api = useApi();
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();

  const [url, setUrl] = useState<Nullable<string>>(null);

  useEffect(() => {
    if (!url) {
      const init = async () => {
        try {
          const { data } = await api.get<TeamBillingPortalDTO>(
            `/my/team/billing/portal`
          );

          if (data && data.url) {
            setUrl(data.url);
          } else {
            setHasError(true);
          }
        } catch (e) {
          setHasError(true);
        }
      };
      init();
    } else {
      const timer = setTimeout(() => {
        window.open(url, "_blank")?.focus();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [url]);

  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div
        className={cn(
          "flex flex-col justify-center items-center space-y-3 h-screen text-center"
        )}
      >
        {hasError ? (
          <SupportMessage />
        ) : (
          <>
            <p className="text-lg font-bold text-center">
              {t("setup.userSelectedIsBillingRole.redirecting")}
            </p>
            <p>
              {t("setup.userSelectedIsBillingRole.manualRedirect")}{" "}
              <a href={url ?? ""} target="_blank" className="underline">
                {t("setup.userSelectedIsBillingRole.clickHere")}
              </a>{" "}
              {t("setup.userSelectedIsBillingRole.accessBillingPortal")}
            </p>
          </>
        )}
      </div>
    </Layout>
  );
};
